/**
 * wd-facets.js — WeDev extended copy of assets/facets.js (facet forms, toolbar + drawer, pill sync).
 * Keep assets/facets.js aligned with the vendor theme; sections load this asset instead.
 */
class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

/** Panel body target for wd toolbar / drawer facet `details` (see `data-wd-facet-details-body` in Liquid). */
function wdGetFacetDetailsBody(details) {
  if (!details || details.tagName !== 'DETAILS') return null;
  return (
    details.querySelector('[data-wd-facet-details-body]') || details.querySelector('summary + *')
  );
}

/**
 * After AJAX, Liquid re-renders full `details` in the response, but `renderFilters` only swaps
 * `[data-wd-facet-details-body]` innerHTML — the pill summary + `details--active` stayed stale.
 */
function wdSyncFacetDetailsChrome(liveDetails, parsedDetails) {
  if (
    !liveDetails ||
    !parsedDetails ||
    liveDetails.tagName !== 'DETAILS' ||
    parsedDetails.tagName !== 'DETAILS'
  ) {
    return;
  }
  const keepOpen = liveDetails.hasAttribute('open');
  liveDetails.className = parsedDetails.className;
  if (keepOpen) liveDetails.setAttribute('open', '');
  else liveDetails.removeAttribute('open');
  const liveSummary = liveDetails.querySelector(':scope > summary');
  const parsedSummary = parsedDetails.querySelector(':scope > summary');
  if (liveSummary && parsedSummary) {
    liveSummary.innerHTML = parsedSummary.innerHTML;
  }
}

/** Mobile drawer: mirror open-panel chips into closed summary (Figma 8082:20023). */
function wdSyncDrawerSummaryChips(details, body) {
  if (!details || !body) return;
  const summary = details.querySelector(':scope > summary.wd-facet-drawer__section-summary');
  if (!summary) return;
  const panelChips = body.querySelector('.wd-toolbar-filter__panel-chips');
  let collapsed = summary.querySelector('.wd-facet-drawer__collapsed-chips');
  const hasSelection = details.classList.contains('wd-toolbar-filter__details--active');
  if (hasSelection && panelChips) {
    if (!collapsed) {
      collapsed = document.createElement('div');
      collapsed.className = 'wd-facet-drawer__collapsed-chips';
      summary.appendChild(collapsed);
    }
    collapsed.innerHTML = panelChips.innerHTML;
  } else if (collapsed) {
    collapsed.remove();
  }
}

/** While `data-wd-defer-facet-submit` is on, pill chrome follows the form until section re-render. */
function wdSyncDeferredFacetPill(details, form) {
  if (!details || !form || !form.hasAttribute('data-wd-defer-facet-submit')) return;
  const body = wdGetFacetDetailsBody(details);
  if (!body) return;
  const showCountBadge = form.matches('form.wd-toolbar-facet-form');
  const isDrawerForm = form.matches('form.wd-facet-drawer__form');
  let count = 0;
  if (details.classList.contains('wd-toolbar-filter__details--price')) {
    const minEl = body.querySelector('input[name="filter.v.price.gte"]');
    const maxEl = body.querySelector('input[name="filter.v.price.lte"]');
    const minV = minEl && String(minEl.value).trim();
    const maxV = maxEl && String(maxEl.value).trim();
    count = minV || maxV ? 1 : 0;
  } else {
    count = body.querySelectorAll('input.wd-toolbar-filter__checkbox-input:checked').length;
  }
  const summary = details.querySelector(':scope > summary.wd-toolbar-filter__pill');
  if (!summary) return;
  const label = summary.querySelector('.wd-toolbar-filter__label');
  const chevron = summary.querySelector('.wd-toolbar-filter__chevron');
  if (count > 0) {
    details.classList.add('wd-toolbar-filter__details--active');
    if (showCountBadge && label && chevron) {
      let badge = summary.querySelector('.wd-toolbar-filter__badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'wd-toolbar-filter__badge';
        label.insertAdjacentElement('afterend', badge);
      }
      badge.textContent = details.classList.contains('wd-toolbar-filter__details--price')
        ? '1'
        : String(count);
    }
  } else {
    details.classList.remove('wd-toolbar-filter__details--active');
    const badge = summary.querySelector('.wd-toolbar-filter__badge');
    if (badge) badge.remove();
  }
  if (isDrawerForm) wdSyncDrawerSummaryChips(details, body);
}

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('input', this.onDeferredFacetInput);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange(event) {
    this.dirty = true;
    /* wedev: drawer sticky CTA + desktop toolbar panel footer CTA submit the form (data-wd-defer-facet-submit + wd-script-collection-toolbar). */
    if (this.hasAttribute('data-wd-defer-facet-submit')) {
      const t = event.target;
      if (t && this.contains(t)) {
        const details = t.closest('details[data-filter].wd-toolbar-filter__details');
        if (details) wdSyncDeferredFacetPill(details, this);
      }
      return;
    }
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onDeferredFacetInput(event) {
    if (!this.hasAttribute('data-wd-defer-facet-submit')) return;
    const t = event.target;
    if (!t || !this.contains(t)) return;
    if (!t.closest('details.wd-toolbar-filter__details--price[data-filter]')) return;
    const details = t.closest('details[data-filter].wd-toolbar-filter__details');
    if (details) wdSyncDeferredFacetPill(details, this);
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Facet fetch failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderFacetFiltersContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      })
      .catch(() => {
        this.afterRenderSection();
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    /* wedev - start */
    const wdFacetFilterSelector =
      typeof window !== 'undefined' &&
      window.__WD_FACET__ &&
      window.__WD_FACET__.SELECTORS &&
      window.__WD_FACET__.SELECTORS.facetFilterRoots
        ? window.__WD_FACET__.SELECTORS.facetFilterRoots
        : '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter], #WdDesktopFacetToolbar [data-filter]';

    const facetElements = parsedHTML.querySelectorAll(wdFacetFilterSelector);
    const facetElementsFormDom = document.querySelectorAll(wdFacetFilterSelector);
    /* wedev - end */

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    /* wedev: chip / "clear all" in wd toolbar or mobile drawer — skipping the matched <details> left chips/panels stale ("magic tag"). */
    const wdSel =
      typeof window !== 'undefined' && window.__WD_FACET__ && window.__WD_FACET__.SELECTORS
        ? window.__WD_FACET__.SELECTORS
        : null;
    const wdDesktop = wdSel && wdSel.wdDesktopToolbar ? wdSel.wdDesktopToolbar : '#WdDesktopFacetToolbar';
    const wdDrawerBody = wdSel && wdSel.drawerFacetBody ? wdSel.drawerFacetBody : '#DrawerFacetFiltersContainer';

    const facetsToRender = Array.from(facetElements).filter((element) => {
      if (!matchesIndex(element)) return true;
      return Boolean(element.closest(wdDesktop) || element.closest(wdDrawerBody));
    });

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          const liveBody = wdGetFacetDetailsBody(filter);
          const nextBody = wdGetFacetDetailsBody(element);
          if (liveBody && nextBody) liveBody.innerHTML = nextBody.innerHTML;
          wdSyncFacetDetailsChrome(filter, element);
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    const target = document.getElementById(id);
    if (target === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);

    target.innerHTML = source ? source.innerHTML : '';
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading') || this.classList.contains('wd-pagination__load-more--loading')) {
      return;
    }
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      })
      .catch(() => {
        this.loadingStateDone();
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');
    if (!pagination) return;

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const action = this.getAttribute('action');
    if (!action) return new URL(window.location.href);
    const url = new URL(action, window.location.href);
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');

    if (this.getAttribute('type') === 'infinite') {
      this.classList.add('wd-pagination__load-more--loading');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.classList.add('btn--loading');
    }
  }

  loadingStateDone() {
    this.classList.remove('pointer-events-none', 'btn--loading', 'wd-pagination__load-more--loading');
    this.removeAttribute('aria-busy');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
